import { useMemo, useState } from 'react';
import { App } from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  FormOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { DataTable, MenuThaoTac, PageHeader, StatTile } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { MauVanBanFormDialog } from '../components/MauVanBanFormDialog';
import { XemMauDialog } from '../components/XemMauDialog';
import { useQuyenVanBan } from '../hooks/useQuyenVanBan';
import { useKhaNangMau, useLuuMau, useMauVanBan, useXemTruocMau, useXoaMau } from '../hooks/useVanBan';
import type { MauVanBan, NguonMau } from '../types';

/** Danh sách mẫu văn bản (`/xu-ly-van-ban/cau-hinh/mau-van-ban`), chuyển từ foxainative. */
export function MauVanBanView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const { data, isLoading, error, refetch } = useMauVanBan();
  const khaNang = useKhaNangMau();
  const luu = useLuuMau();
  const xemTruoc = useXemTruocMau();
  const xoa = useXoaMau();

  const { canCreate, canUpdate, canDelete } = useQuyenVanBan('template');

  const [tuKhoa, setTuKhoa] = useState('');
  const [nguon, setNguon] = useState<NguonMau | undefined>();
  const [xemId, setXemId] = useState<string | null>(null);
  const [moForm, setMoForm] = useState(false);
  const [dangSua, setDangSua] = useState<MauVanBan | null>(null);

  const tatCa = useMemo(
    () => [...(data?.data ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [data],
  );
  const rows = useMemo(() => {
    const q = tuKhoa.trim().toLowerCase();
    return tatCa.filter(
      (m) =>
        (!q || m.name.toLowerCase().includes(q) || (m.description ?? '').toLowerCase().includes(q)) &&
        (!nguon || m.source === nguon),
    );
  }, [tatCa, tuKhoa, nguon]);
  // Bản đang xem luôn đọc lại từ cache: tạo xem trước xong thì hộp thoại thấy ngay nội dung mới
  const dangXem = tatCa.find((m) => m.id === xemId) ?? null;
  const coLoc = Boolean(tuKhoa.trim() || nguon);
  const thongKe = data?.stats;
  const so = (n: number | undefined) => soVN(n ?? 0, locale);

  const moThem = () => {
    luu.reset();
    setDangSua(null);
    setMoForm(true);
  };
  const moSua = (m: MauVanBan) => {
    luu.reset();
    setXemId(null);
    setDangSua(m);
    setMoForm(true);
  };
  const hoiXoa = (m: MauVanBan) => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: t('vanBanMau.xacNhanXoa', { ten: m.name }),
      content: t(m.source === 'NGHI_DINH_30' ? 'vanBanMau.xacNhanXoaNd30' : 'vanBanMau.xacNhanXoaMo'),
      okText: t('vanBanMau.xoa'),
      okButtonProps: { danger: true },
      cancelText: t('chung.huy'),
      onOk: () => xoa.mutateAsync(m.id),
    });
  };

  const columns: TableProps<MauVanBan>['columns'] = [
    {
      title: t('chung.stt'),
      width: 64,
      render: (_v, _r, i) => <span className="text-xs tabular-nums text-content-muted">{i + 1}</span>,
    },
    {
      title: t('vanBanMau.cot.ten'),
      width: 360,
      // Bảng cuộn ngang theo nội dung (DataTable, x: max-content): phải chặn bề rộng thì mô tả mới cắt được
      render: (_v, m) => (
        <div className="min-w-0 max-w-xs">
          <div className="font-medium text-content-primary">{m.name}</div>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-content-muted">
            <span className="truncate">{m.description || t('chung.khongCo')}</span>
            {m.original_filename && (
              <span className="inline-flex shrink-0 items-center gap-1 text-primary-600">
                <PaperClipOutlined /> {m.original_filename}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('vanBanMau.cot.kyHieu'),
      width: 144,
      render: (_v, m) => <Badge variant="neutral">{t('vanBanMau.kyHieuMau', { ma: m.symbol_code })}</Badge>,
    },
    {
      title: t('vanBanMau.cot.nguon'),
      width: 210,
      render: (_v, m) =>
        m.source === 'NGHI_DINH_30' ? (
          <Badge variant="info" className="whitespace-nowrap">{t('vanBanMau.nguonNd30')}</Badge>
        ) : (
          <Badge variant="orange">{t('vanBanMau.nguonTuyChinh')}</Badge>
        ),
    },
    {
      title: t('chung.trangThai'),
      width: 130,
      render: (_v, m) =>
        m.status === 'DANG_DUNG' ? (
          <Badge variant="success">{t('vanBanMau.trangThai.DANG_DUNG')}</Badge>
        ) : (
          <Badge variant="neutral">{t('vanBanMau.trangThai.NGUNG_DUNG')}</Badge>
        ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      render: (_v, m) => (
        <div onClick={(e) => e.stopPropagation()}>
          <MenuThaoTac
            tenBanGhi={m.name}
            muc={[
              { khoa: 'xem', nhan: t('vanBanMau.xem'), icon: <EyeOutlined className="text-violet-500" />, nhom: 'xem', onChon: () => setXemId(m.id) },
              { khoa: 'sua', nhan: t('vanBanMau.sua'), icon: <EditOutlined className="text-primary-600" />, hien: canUpdate, nhom: 'sua', onChon: () => moSua(m) },
              { khoa: 'xoa', nhan: t('vanBanMau.xoa'), icon: <DeleteOutlined />, hien: canDelete, nhom: 'pha-huy', onChon: () => hoiXoa(m) },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.xuLyVanBan') },
            { label: t('nav.muc.cauHinhXuLyVanBan') },
            { label: t('vanBanMau.tieuDe') },
          ]}
          titleIcon={<SnippetsOutlined className="text-primary-600" />}
          title={t('vanBanMau.tieuDe')}
          subtitle={t('vanBanMau.moTa')}
          actions={
            <>
              <Button variant="ghost" title={t('chung.lamMoi')} aria-label={t('chung.lamMoi')} onClick={() => void refetch()}>
                <ReloadOutlined className={isLoading ? 'animate-spin' : ''} />
              </Button>
              {canCreate && (
                <Button variant="primary" icon={<PlusOutlined />} onClick={moThem}>
                  {t('vanBanMau.them')}
                </Button>
              )}
            </>
          }
        />

        <div className="grid grid-cols-12 gap-4">
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanMau.thongKe.tong')} so={so(thongKe?.total)} icon={<SnippetsOutlined />} tone="primary" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanMau.thongKe.nd30')} so={so(thongKe?.nghi_dinh_30)} icon={<FileTextOutlined />} tone="sky" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanMau.thongKe.tuyChinh')} so={so(thongKe?.tuy_chinh)} icon={<FormOutlined />} tone="violet" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanMau.thongKe.dangDung')} so={so(thongKe?.dang_dung)} icon={<CheckCircleOutlined />} tone="success" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Input
            className="min-w-[200px] max-w-sm flex-1"
            allowClear
            prefix={<SearchOutlined className="text-primary-500" />}
            placeholder={t('vanBanMau.timTheo')}
            aria-label={t('vanBanMau.timTheo')}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
          />
          <Select
            allowClear
            className="min-w-[12rem]"
            placeholder={t('vanBanMau.tatCaNguon')}
            aria-label={t('vanBanMau.locNguon')}
            options={[
              { value: 'NGHI_DINH_30', label: t('vanBanMau.nguonNd30') },
              { value: 'TUY_CHINH', label: t('vanBanMau.nguonTuyChinh') },
            ]}
            value={nguon}
            onChange={(v) => setNguon(v as NguonMau | undefined)}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {error ? (
            <div role="alert" className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm text-danger-700">{t('vanBanMau.loiDanhSach', { loi: dichLoi(error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <DataTable<MauVanBan>
              caption={t('vanBanMau.bangCaption')}
              rowKey="id"
              dataSource={rows}
              columns={columns}
              loading={isLoading}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              onRow={(m) => ({ onClick: () => setXemId(m.id), className: 'cursor-pointer' })}
              locale={{
                emptyText: isLoading ? (
                  ' '
                ) : (
                  <Empty
                    icon={SnippetsOutlined}
                    title={t(coLoc ? 'vanBanMau.rongLoc' : 'vanBanMau.rongThat')}
                    description={coLoc ? undefined : t('vanBanMau.rongThatMo')}
                    action={
                      coLoc || !canCreate ? undefined : (
                        <Button variant="primary" icon={<PlusOutlined />} onClick={moThem}>
                          {t('vanBanMau.them')}
                        </Button>
                      )
                    }
                  />
                ),
              }}
            />
          )}
        </div>
      </div>

      <XemMauDialog
        mau={dangXem}
        coAi={khaNang.data?.ai_preview ?? false}
        dangTao={xemTruoc.isPending}
        coTheSua={canUpdate}
        coTheTao={canUpdate}
        onClose={() => setXemId(null)}
        onSua={moSua}
        onTaoXemTruoc={(m) => xemTruoc.mutate(m.id)}
        onSoanThao={(m) => navigate(`/xu-ly-van-ban/soan-thao?tpl=${encodeURIComponent(m.id)}`)}
      />

      <MauVanBanFormDialog
        open={moForm}
        mau={dangSua}
        saving={luu.isPending}
        loiMayChu={luu.error}
        onClose={() => setMoForm(false)}
        onSubmit={(vao) => luu.mutate({ id: dangSua?.id ?? null, vao }, { onSuccess: () => setMoForm(false) })}
      />
    </div>
  );
}
